/**
 * The photo drop box on the sports import modal (djobs/_photography_admin and
 * _photography_nonadmin): drag photos or a folder onto it, or click it to pick
 * a folder as before.
 *
 * Whichever way they arrive, the selection follows the rule the folder picker
 * always had: the files directly in the folder (or the files themselves, when
 * files rather than a folder are given), never what sits in a subfolder --
 * photographers' folders carry edits and exports in subfolders. Non-JPGs are
 * split off by splitJpgs, as before.
 *
 * Installed once, from packs/application.js, by delegation on the document,
 * so a zone rendered later (a modal loaded over AJAX) works without any
 * per-page start-up. A zone is any element with data-import-drop="<input id>"
 * naming its hidden <input type="file">; its selection is kept on the element
 * as `importSelection` ({ files, subfolderSkipped }) for the upload code.
 *
 * One modal takes batch after batch (three volleyball teams under one job
 * code). When a batch finishes the upload code calls batchFinished: the zone
 * forgets that batch, so Upload can't send it twice. The next drop re-arms the
 * modal -- inside the zone's [data-import-scope], the [data-import-upload]
 * button is enabled and relabelled from its attribute, [data-import-cancel] is
 * relabelled the same way, and [data-import-status] (the last batch's results)
 * is cleared.
 */

const isJpg = (name) => /\.jpe?g$/i.test(name);

/** The folder picker's FileList: top level only (webkitRelativePath depth 2). */
export function topLevelFromPicker(fileList) {
  const files = [];
  let subfolderSkipped = 0;
  Array.from(fileList || []).forEach((file) => {
    const rel = file.webkitRelativePath || '';
    if (rel === '' || rel.split('/').length === 2) files.push(file);
    else subfolderSkipped += 1;
  });
  return { files, subfolderSkipped };
}

/** { jpgs, nonJpgCount } -- the photographers' folders hold PNG edits, .DS_Store and the like. */
export function splitJpgs(files) {
  const jpgs = (files || []).filter((file) => isJpg(file.name));
  return { jpgs, nonJpgCount: (files || []).length - jpgs.length };
}

// A directory reader hands back entries in batches (100 in Chrome) until an
// empty batch: one call reads only the first batch of a big folder.
function readAllEntries(directory) {
  const reader = directory.createReader();
  const all = [];
  return new Promise((resolve, reject) => {
    const next = () => reader.readEntries((batch) => {
      if (batch.length === 0) {
        resolve(all);
      } else {
        all.push(...batch);
        next();
      }
    }, reject);
    next();
  });
}

const fileOf = (entry) => new Promise((resolve, reject) => entry.file(resolve, reject));

async function countFiles(directory) {
  const entries = await readAllEntries(directory);
  const counts = await Promise.all(entries.map((entry) => (entry.isDirectory ? countFiles(entry) : 1)));
  return counts.reduce((sum, n) => sum + n, 0);
}

/**
 * The files dropped: a dropped file as itself, a dropped folder's top-level
 * files, and a count of the files left behind in subfolders.
 */
export async function filesFromDrop(dataTransfer) {
  const items = Array.from((dataTransfer && dataTransfer.items) || []);
  // Read every entry before awaiting anything: the browser empties
  // dataTransfer.items once the drop handler yields.
  const entries = items.map((item) => item.webkitGetAsEntry && item.webkitGetAsEntry()).filter(Boolean);
  if (entries.length === 0) {
    return { files: Array.from((dataTransfer && dataTransfer.files) || []), subfolderSkipped: 0 };
  }

  const files = [];
  let subfolderSkipped = 0;
  // eslint-disable-next-line no-restricted-syntax
  for (const entry of entries) {
    if (entry.isFile) {
      // eslint-disable-next-line no-await-in-loop
      files.push(await fileOf(entry));
    } else if (entry.isDirectory) {
      // eslint-disable-next-line no-await-in-loop
      const children = await readAllEntries(entry);
      // eslint-disable-next-line no-restricted-syntax
      for (const child of children) {
        if (child.isFile) {
          // eslint-disable-next-line no-await-in-loop
          files.push(await fileOf(child));
        } else if (child.isDirectory) {
          // eslint-disable-next-line no-await-in-loop
          subfolderSkipped += await countFiles(child);
        }
      }
    }
  }
  return { files, subfolderSkipped };
}

/** What the zone says once something is chosen. */
export function selectionSummary({ files, subfolderSkipped }) {
  const { jpgs, nonJpgCount } = splitJpgs(files);
  const parts = [`${jpgs.length} JPG${jpgs.length === 1 ? '' : 's'} ready to upload`];
  if (nonJpgCount > 0) parts.push(`${nonJpgCount} non-JPG file(s) will be skipped`);
  if (subfolderSkipped > 0) parts.push(`${subfolderSkipped} file(s) in subfolders skipped`);
  return parts.join(' · ');
}

export const UNREADABLE = 'Those files could not be read -- try dropping them again, or click to choose the folder.';

const EMPTY = () => ({ files: [], subfolderSkipped: 0 });

function keep(zone, selection, message) {
  // eslint-disable-next-line no-param-reassign
  zone.importSelection = selection;
  const summary = zone.querySelector('[data-import-drop-summary]');
  if (summary) summary.textContent = message;
}

export const NEXT_BATCH = 'Uploaded. Drop the next batch here.';

/**
 * The batch is in: forget it, and say the zone is ready for the next one.
 * `uploaded` is the selection that was sent. A batch dropped while that one was
 * still running is a different selection: keep it, and give Upload back for it
 * (the finished batch's results stay on screen).
 */
export function batchFinished(zone, uploaded) {
  if (!zone) return;
  if (uploaded && zone.importSelection !== uploaded) {
    // eslint-disable-next-line no-use-before-define
    rearm(zone, { keepStatus: true });
    return;
  }
  keep(zone, EMPTY(), NEXT_BATCH);
}

/** A new batch: the finished one's Done/Close buttons and results give way. */
function rearm(zone, { keepStatus = false } = {}) {
  const scope = zone.closest('[data-import-scope]');
  if (!scope) return;
  const upload = scope.querySelector('[data-import-upload]');
  if (upload) {
    upload.disabled = false;
    upload.textContent = upload.getAttribute('data-import-upload');
  }
  const cancel = scope.querySelector('[data-import-cancel]');
  if (cancel) cancel.textContent = cancel.getAttribute('data-import-cancel');
  const status = scope.querySelector('[data-import-status]');
  if (status && !keepStatus) status.innerHTML = '';
}

function choose(zone, selection, message = selectionSummary(selection)) {
  if (selection.files.length > 0) rearm(zone);
  keep(zone, selection, message);
  zone.dispatchEvent(new CustomEvent('import-files-selected', { bubbles: true, detail: selection }));
}

const zoneFor = (target) => (target && target.closest ? target.closest('[data-import-drop]') : null);

/** Installs the delegated listeners once. Returns false when already installed. */
export function installImportDropZones(doc = document) {
  if (doc.importDropZonesInstalled) return false;
  // eslint-disable-next-line no-param-reassign
  doc.importDropZonesInstalled = true;

  ['dragenter', 'dragover'].forEach((type) => doc.addEventListener(type, (event) => {
    const zone = zoneFor(event.target);
    if (!zone) return;
    event.preventDefault(); // without this the browser opens the photo instead
    zone.classList.add('import-drop-active');
  }));

  doc.addEventListener('dragleave', (event) => {
    const zone = zoneFor(event.target);
    if (zone && !zone.contains(event.relatedTarget)) zone.classList.remove('import-drop-active');
  });

  doc.addEventListener('drop', async (event) => {
    const zone = zoneFor(event.target);
    if (!zone) return;
    event.preventDefault();
    zone.classList.remove('import-drop-active');
    // An unreadable entry rejects. Clear the selection rather than leave the
    // previous one showing -- Upload would quietly send those old files.
    try {
      choose(zone, await filesFromDrop(event.dataTransfer));
    } catch (e) {
      choose(zone, EMPTY(), UNREADABLE);
    }
  });

  doc.addEventListener('click', (event) => {
    const zone = zoneFor(event.target);
    if (!zone) return;
    const input = doc.getElementById(zone.getAttribute('data-import-drop'));
    if (input && event.target !== input) input.click();
  });

  doc.addEventListener('keydown', (event) => {
    const zone = zoneFor(event.target);
    if (!zone || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    const input = doc.getElementById(zone.getAttribute('data-import-drop'));
    if (input) input.click();
  });

  doc.addEventListener('change', (event) => {
    const input = event.target;
    if (!input || !input.id) return;
    const zone = doc.querySelector(`[data-import-drop="${input.id}"]`);
    if (zone) choose(zone, topLevelFromPicker(input.files));
  });

  return true;
}
